// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Theme, useTheme } from "@mui/material/styles";
import NavbarTextButton from "src/components/shared/navigation/Navbar/NavbarTextButton/NavbarTextButton";
import SharedTabs from "src/components/shared/navigation/SharedTabs/SharedTabs";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import FormField from "src/pages/EmrConnections/FormField/FormField";
import WizardSteps from "src/pages/EmrConnections/WizardSteps/WizardSteps";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  ChevronDownIcon,
  CloseIcon,
  DownloadIcon,
  EditIcon,
  ErrorIcon,
  HeartbeatIcon,
  PlusIcon,
  SuccessIcon,
  TrashIcon,
  WarningIcon,
} from "src/assets/icons";
import eyeLogo from "src/assets/images/logos/eyelogo.svg";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { guideTarget } from "../Walkthrough/walkthroughState";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  ModalCustomConfirmTeal,
  ModalCustomDelete,
  ModalCustomSurfaceLink,
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

export const noop = () => undefined;

export const mockCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

export const mockCardBorder = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? "1px solid #5B6D7A"
    : "1px solid rgba(0, 0, 0, 0.2)";

export const mockTileBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20";

const target = (id?: string, inner?: string) =>
  id ? guideTarget(id, inner) : {};

type NavTab = "Dashboard" | "Patients" | "Data Hub";

export const MockNavbar = ({
  current,
  dataHubTarget,
}: {
  current: NavTab;
  dataHubTarget?: string;
}) => {
  const { t } = useTranslation();
  const tabs: Array<NavTab> = ["Dashboard", "Patients", "Data Hub"];

  return (
    <Box
      sx={{
        backgroundColor: "#202338",
        color: "#FFF",
        display: "flex",
        alignItems: "center",
        gap: { xs: "8px", sm: "18px" },
        padding: "12px 18px",
        borderRadius: "8px 8px 0 0",
        flexWrap: "wrap",
      }}
    >
      <Box
        component="img"
        src={eyeLogo}
        alt=""
        sx={{ width: { xs: 40, sm: 56 }, marginRight: "8px" }}
      />
      {tabs.map((tab) => (
        <Box key={tab} {...(tab === "Data Hub" ? target(dataHubTarget) : {})}>
          <NavbarTextButton
            dataTestId={`guide-nav-${tab}`}
            title={t(tab)}
            onClick={noop}
            isUnderlined={tab === current}
            showTab
          />
        </Box>
      ))}
    </Box>
  );
};

export const MockProblemBanner = ({
  text,
  viewTarget,
  bannerTarget,
}: {
  text: string;
  viewTarget?: string;
  bannerTarget?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      {...target(bannerTarget)}
      sx={{
        backgroundColor: "#E2772E",
        color: "#FFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "7px 12px",
        fontSize: "13px",
        flexWrap: "wrap",
        textAlign: "center",
      }}
    >
      <WarningIcon width="18px" height="18px" />
      {text}
      <Box
        component="span"
        {...target(viewTarget)}
        sx={{ textDecoration: "underline", fontWeight: 700 }}
      >
        {t("View Hub")}
      </Box>
    </Box>
  );
};

export const MockPage = ({
  children,
  attached = false,
}: {
  children: ReactNode;
  attached?: boolean;
}) => {
  const theme = useTheme();
  const { pageWash, dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      sx={{
        backgroundColor: pageWash,
        border: dividerBorder,
        borderTop: attached ? 0 : dividerBorder,
        borderRadius: attached ? "0 0 8px 8px" : "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      {children}
    </Box>
  );
};

export const MockDataHubTabs = ({
  current,
  configTarget,
  processedTarget,
}: {
  current: 0 | 1;
  configTarget?: string;
  processedTarget?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Box {...target(processedTarget, "button:nth-of-type(2)")}>
      <Box {...target(configTarget, "button:nth-of-type(1)")}>
        <SharedTabs
          tabs={[
            { id: 0, label: t("Data Hub Config"), showTab: true },
            { id: 1, label: t("Processed Exams"), showTab: true },
          ]}
          selectedTabId={current}
          onChange={noop}
          tabColor={ACCENT_TEAL}
        />
      </Box>
    </Box>
  );
};

type MockButtonKind = "confirm" | "teal" | "cancel" | "delete" | "link";

const BUTTON_STYLES: Record<MockButtonKind, (theme: Theme) => object> = {
  confirm: () => ModalCustomConfirm(),
  teal: () => ModalCustomConfirmTeal(),
  cancel: (theme) => ModalCustomCancel(theme),
  delete: (theme) => ModalCustomDelete(theme),
  link: (theme) => ModalCustomSurfaceLink(theme),
};

export const MockButton = ({
  kind,
  children,
  startIcon,
  disabled = false,
  targetId,
  small = false,
}: {
  kind: MockButtonKind;
  children: ReactNode;
  startIcon?: ReactNode;
  disabled?: boolean;
  targetId?: string;
  small?: boolean;
}) => {
  const theme = useTheme();

  return (
    <Button
      {...target(targetId)}
      disableRipple
      tabIndex={-1}
      disabled={disabled}
      startIcon={startIcon}
      sx={{
        ...BUTTON_STYLES[kind](theme),
        ...(small && { height: "30px", padding: "0 10px" }),
      }}
    >
      {children}
    </Button>
  );
};

export const MockSnack = ({
  text,
  variant = "success",
  targetId,
}: {
  text: string;
  variant?: "success" | "error";
  targetId?: string;
}) => {
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const color = variant === "success" ? "#22BE39" : "#E10C0C";

  return (
    <Box sx={{ alignSelf: "flex-start", backgroundColor: surface }}>
      <Box
        {...target(targetId)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "8px 16px",
          border: `1px solid ${color}`,
          borderRadius: "4px",
          color: onSurface,
          backgroundColor: `${color}40`,
          fontSize: "14px",
        }}
      >
        <Box display="flex" color={color}>
          {variant === "success" ? <SuccessIcon /> : <ErrorIcon />}
        </Box>
        {text}
      </Box>
    </Box>
  );
};

export const MockTooltip = ({ text }: { text: string }) => (
  <Box
    component="span"
    sx={{
      position: "absolute",
      bottom: "calc(100% + 6px)",
      right: "-6px",
      backgroundColor: "#616161",
      color: "#FFF",
      fontSize: "11px",
      padding: "4px 8px",
      borderRadius: "4px",
      whiteSpace: "nowrap",
      zIndex: 5,
    }}
  >
    {text}
  </Box>
);

export const MockIconButton = ({
  children,
  color,
  targetId,
  tooltip,
}: {
  children: ReactNode;
  color?: string;
  targetId?: string;
  tooltip?: string;
}) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  return (
    <Box
      {...target(targetId)}
      sx={{
        position: "relative",
        width: "40px",
        height: "40px",
        display: "inline-grid",
        placeItems: "center",
        color: color ?? onSurface,
      }}
    >
      {tooltip && <MockTooltip text={tooltip} />}
      {children}
    </Box>
  );
};

export const DataAgentsCard = ({
  addTarget,
  children,
}: {
  addTarget?: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: mockCardBackground(theme),
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap="8px">
          <HeartbeatIcon style={{ color: ACCENT_TEAL }} />
          <Typography fontSize="16px" fontWeight={600} color={ACCENT_TEAL}>
            {t("Data Agents")}
          </Typography>
        </Box>
        <MockButton kind="teal" startIcon={<PlusIcon />} targetId={addTarget}>
          {t("Add")}
        </MockButton>
      </Box>
      {children}
    </Box>
  );
};

export const MockTile = ({
  label,
  value,
  valueColor,
  targetId,
  grow = false,
}: {
  label: ReactNode;
  value: ReactNode;
  valueColor?: string;
  targetId?: string;
  grow?: boolean;
}) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  return (
    <Box
      {...target(targetId)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px",
        borderRadius: "4px",
        minWidth: 0,
        backgroundColor: mockTileBackground(theme),
        ...(grow ? { flex: "1 1 70px" } : { width: "100%" }),
      }}
    >
      <Typography component="div" fontSize="12px">
        {label}
      </Typography>
      <Typography
        component="div"
        fontSize="14px"
        fontWeight={600}
        color={valueColor ?? onSurface}
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const MockStatusChip = ({
  text,
  color,
  targetId,
}: {
  text: string;
  color: string;
  targetId?: string;
}) => (
  <Typography
    {...target(targetId)}
    sx={{
      flexShrink: 0,
      color,
      fontSize: "14px",
      fontWeight: 600,
      border: `1px solid ${color}`,
      borderRadius: "4px",
      padding: "0px 4px",
    }}
  >
    {text}
  </Typography>
);

export const MockUpdateChip = ({
  version,
  targetId,
}: {
  version: string;
  targetId?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Typography
      {...target(targetId)}
      fontSize="12px"
      fontWeight={700}
      sx={{
        color: "#E2772E",
        border: "1px solid #E2772E",
        borderRadius: "4px",
        padding: "1px 6px",
      }}
    >
      {t("Update {{version}} available", { version })}
    </Typography>
  );
};

export type HubCardTargets = Partial<
  Record<
    | "status"
    | "alias"
    | "created"
    | "clinics"
    | "network"
    | "needs"
    | "queue"
    | "problems"
    | "version"
    | "connections"
    | "jobs"
    | "download"
    | "edit"
    | "delete",
    string
  >
>;

export const MockHubCard = ({
  status,
  problems = 0,
  clinics,
  network,
  needs = 0,
  queue = 0,
  version,
  update,
  tooltip,
  targets = {},
  compact = false,
}: {
  status: "Pending" | "Active" | "Revoked";
  problems?: number;
  clinics?: string;
  network?: string;
  needs?: number;
  queue?: number;
  version?: string;
  update?: string;
  tooltip?: { on: "download" | "edit" | "delete"; text: string };
  targets?: HubCardTargets;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  const statusColor =
    status === "Revoked"
      ? errorColor
      : status === "Pending" || problems > 0
        ? "#E2772E"
        : "#22BE39";

  const statusText =
    problems > 0 && status !== "Revoked"
      ? `${t(status)} · ${t("{{count}} problem", { count: problems })}`
      : t(status);

  return (
    <Box
      sx={{
        border: mockCardBorder(theme),
        backgroundColor: mockCardBackground(theme),
        borderRadius: "8px",
        width: "100%",
        maxWidth: "520px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          borderBottom: mockCardBorder(theme),
          flexWrap: "wrap",
        }}
      >
        <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
          <MockStatusChip
            text={statusText}
            color={statusColor}
            targetId={targets.status}
          />
          <Typography
            {...target(targets.alias)}
            fontSize="14px"
            fontWeight={600}
            color={onSurface}
          >
            {tg("Front desk PC")}
          </Typography>
        </Box>
        <Typography {...target(targets.created)} fontSize="12px">
          {t("Created")}: 2026-10-19
        </Typography>
      </Box>

      {!compact && (
        <Box display="flex" flexDirection="column" gap="8px" padding="8px">
          {clinics && (
            <MockTile
              label={t("Associated Clinics")}
              value={clinics}
              targetId={targets.clinics}
            />
          )}
          {network !== undefined && (
            <MockTile
              label={
                <>
                  {t("Network Interfaces")}{" "}
                  <span style={{ fontWeight: 600 }}>(MAC : IPv4)</span>
                </>
              }
              value={network || "N/A"}
              targetId={targets.network}
            />
          )}
          <Box display="flex" gap="8px">
            <MockTile
              grow
              label={t("Needs Attention")}
              value={needs}
              targetId={targets.needs}
            />
            <MockTile
              grow
              label={t("Queue")}
              value={queue}
              targetId={targets.queue}
            />
            <MockTile
              grow
              label={t("Problems")}
              value={problems}
              valueColor={problems ? errorColor : undefined}
              targetId={targets.problems}
            />
          </Box>
          <Box
            {...target(targets.version)}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap="8px"
            flexWrap="wrap"
          >
            {update && <MockUpdateChip version={update} />}
            <Typography fontSize="12px">
              {t("App Version")}: {version ?? "N/A"}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px",
          borderTop: compact ? 0 : mockCardBorder(theme),
          flexWrap: "wrap",
        }}
      >
        <Box display="flex" alignItems="center" gap="16px" height="40px">
          <MockButton kind="link" targetId={targets.connections}>
            {t("Connections")}
          </MockButton>
          <MockButton kind="link" targetId={targets.jobs}>
            {t("Jobs")}
          </MockButton>
        </Box>
        <Box display="flex">
          <MockIconButton
            color={update ? "#E2772E" : undefined}
            targetId={targets.download}
            tooltip={tooltip?.on === "download" ? tooltip.text : undefined}
          >
            <DownloadIcon />
          </MockIconButton>
          {status !== "Revoked" && (
            <MockIconButton
              targetId={targets.edit}
              tooltip={tooltip?.on === "edit" ? tooltip.text : undefined}
            >
              <EditIcon />
            </MockIconButton>
          )}
          {status !== "Revoked" && (
            <MockIconButton
              color={errorColor}
              targetId={targets.delete}
              tooltip={tooltip?.on === "delete" ? tooltip.text : undefined}
            >
              <TrashIcon />
            </MockIconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const MockDialog = ({
  title,
  steps,
  current = 0,
  children,
  footer,
  maxWidth = "560px",
}: {
  title: string;
  steps?: Array<string>;
  current?: number;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}) => {
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        borderRadius: "8px",
        padding: { xs: "16px 8px", sm: "22px 14px" },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth,
          borderRadius: "4px",
          overflow: "hidden",
          color: onSurface,
          backgroundColor: isDark ? "#5C6076" : surface,
          boxShadow: "0 11px 15px -7px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#202338",
            color: "#FFF",
            padding: "12px 16px",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {title}
          <CloseIcon />
        </Box>
        {steps && <WizardSteps steps={steps} current={current} />}
        <Box display="flex" flexDirection="column" gap="16px" padding="16px">
          {children}
        </Box>
        {footer && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: "wrap",
              padding: "16px",
              borderTop: isDark ? "1px solid #2A2E44" : "1px solid #435B6D7A",
              backgroundColor: isDark ? "#5C6076" : surface,
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const MockField = ({
  label,
  value,
  placeholder = "",
  filled = true,
  required = false,
  helper,
  targetId,
}: {
  label: string;
  value: string;
  placeholder?: string;
  filled?: boolean;
  required?: boolean;
  helper?: string;
  targetId?: string;
}) => (
  <Box {...target(targetId)} minWidth={0}>
    <FormField label={label} required={required}>
      <TextInputField
        placeholder={placeholder}
        value={filled ? value : ""}
        helperText={helper}
        onChange={noop}
      />
    </FormField>
  </Box>
);

export const MockSelect = ({
  label,
  value,
  placeholder,
  filled = true,
  required = false,
  targetId,
}: {
  label: string;
  value: string;
  placeholder?: string;
  filled?: boolean;
  required?: boolean;
  targetId?: string;
}) => {
  const theme = useTheme();
  const { inputBackground, inputBorder, mutedText } = getSurfaceTokens(theme);

  return (
    <Box {...target(targetId)} minWidth={0}>
      <FormField label={label} required={required}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            height: "33px",
            padding: "0 12px",
            borderRadius: "4px",
            fontSize: "14px",
            border: `1px solid ${inputBorder}`,
            backgroundColor: inputBackground,
          }}
        >
          <Box component="span" color={filled ? undefined : mutedText}>
            {filled ? value : placeholder}
          </Box>
          <ChevronDownIcon width="16px" height="16px" />
        </Box>
      </FormField>
    </Box>
  );
};

export const MockRow = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "12px",
    }}
  >
    {children}
  </Box>
);

export const MockQuestion = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => {
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  return (
    <Box display="flex" flexDirection="column" gap="4px">
      <Typography fontSize="15px" fontWeight={800}>
        {title}
      </Typography>
      {subtitle && (
        <Typography fontSize="12px" color={mutedText} lineHeight={1.45}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export const MockTogglePanel = ({
  title,
  description,
  isOn,
  targetId,
  children,
}: {
  title: string;
  description: string;
  isOn: boolean;
  targetId?: string;
  children?: ReactNode;
}) => {
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      {...target(targetId)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        borderRadius: "8px",
        padding: isOn ? "11px" : "12px",
        border: isOn
          ? `2px solid ${ACCENT_TEAL}`
          : `1px solid ${isDark ? "#8A8DA2" : "rgba(0, 0, 0, 0.2)"}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" gap="10px">
        <Box minWidth={0}>
          <Typography fontSize="14px" fontWeight={700}>
            {title}
          </Typography>
          <Typography fontSize="12px" color={mutedText} mt="2px">
            {description}
          </Typography>
        </Box>
        <CustomSwitch checked={isOn} onChange={noop} small />
      </Box>
      {isOn && children}
    </Box>
  );
};

export const MockMoreOptions = ({ targetId }: { targetId?: string }) => {
  const { t } = useTranslation();

  return (
    <Typography
      {...target(targetId)}
      fontSize="13px"
      fontWeight={700}
      color={ACCENT_TEAL}
      alignSelf="flex-start"
    >
      {t("More options")}
    </Typography>
  );
};

export const MockCheckbox = ({
  label,
  checked,
  targetId,
}: {
  label: string;
  checked: boolean;
  targetId?: string;
}) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  return (
    <Box
      {...target(targetId)}
      display="flex"
      alignItems="center"
      gap="8px"
      fontSize="13px"
    >
      <Box
        sx={{
          width: "18px",
          height: "18px",
          borderRadius: "3px",
          border: `2px solid ${checked ? ACCENT_TEAL : onSurface}`,
          backgroundColor: checked ? ACCENT_TEAL : "transparent",
          color: "#FFF",
          display: "grid",
          placeItems: "center",
          fontSize: "12px",
          lineHeight: 1,
        }}
      >
        {checked ? "✓" : ""}
      </Box>
      {label}
    </Box>
  );
};
